/**
 * @module api/client
 * @description
 *   Shared axios instance for CriticAI API calls (session cookies, Firebase
 *   ID token, timeouts, logging).
 *
 * @extracted-from client/src/App.js (lines ~23–57, Phase 7)
 *
 * @invariants
 *   - baseURL from apiConfig.js unchanged
 *   - withCredentials: true for cross-origin session cookies
 *   - Authorization: Bearer <firebaseIdToken> when signed in
 *
 * @dependencies
 *   - axios, apiConfig, authHeaders
 */

import axios from 'axios';
import { API_BASE_URL } from '../apiConfig';
import { getFirebaseAuthHeaders } from './authHeaders';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 540000, // 9 minute timeout — above Node→Python 8 min wait plus review generation
  withCredentials: true, // session cookie for API when client is on another origin
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  async (config) => {
    config.metadata = { startTime: new Date() };
    try {
      const authHeaders = await getFirebaseAuthHeaders();
      if (authHeaders.Authorization) {
        config.headers = config.headers || {};
        config.headers.Authorization = authHeaders.Authorization;
      }
    } catch (err) {
      console.warn('api client: could not attach Firebase token', err?.message || err);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    const duration = new Date() - response.config.metadata.startTime;
    console.log(`API call to ${response.config.url} took ${duration}ms`);
    return response;
  },
  (error) => {
    const duration = new Date() - (error.config?.metadata?.startTime || new Date());
    console.error(`API call to ${error.config?.url} failed after ${duration}ms:`, error);
    return Promise.reject(error);
  }
);

export default api;
